import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CreditCard, Check, AlertCircle, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { businessPageAccess } from '../../lib/roles';
import { businessApi, errorMessage } from '../../lib/api';
import BusinessHeader from '../../components/business/BusinessHeader';

// Phase 1 (ADR-0009 PD-4/PD-5): plan changes, card updates and cancellation happen in
// the Stripe Customer Portal. This page shows the current plan and opens the portal.
const ManageSubscription = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState(null);

  const hasBusinessAccess = businessPageAccess(user);

  useEffect(() => {
    if (!authLoading && (!isAuthenticated || !hasBusinessAccess)) navigate('/');
  }, [isAuthenticated, authLoading, hasBusinessAccess, navigate]);

  useEffect(() => {
    if (!isAuthenticated) return;
    (async () => {
      try {
        const [subRes, tiersRes] = await Promise.all([
          businessApi.get('/business/subscription'),
          businessApi.get('/subscription-tiers'),
        ]);
        setSubscription(subRes.data);
        setTiers(tiersRes.data?.tiers || []);
      } catch (e) {
        console.error('Error loading subscription:', e);
        setError('Could not load your subscription.');
      } finally {
        setLoading(false);
      }
    })();
  }, [isAuthenticated]);

  const openPortal = async () => {
    setOpening(true);
    setError(null);
    try {
      const res = await businessApi.post('/stripe/portal-session', {});
      window.location.href = res.data.url;
    } catch (e) {
      setError(errorMessage(e, 'Could not open the billing portal.'));
      setOpening(false);
    }
  };

  const tier = tiers.find(t => t.tier_key === subscription?.plan_tier);
  const planName = tier?.name || (subscription?.plan_tier ? subscription.plan_tier.charAt(0).toUpperCase() + subscription.plan_tier.slice(1) : 'Free');
  // Anyone with a Stripe customer (active, past_due, cancel-pending) manages billing in the portal
  const isPaid = !!subscription?.has_billing_portal;
  const status = subscription?.subscription_status;
  // 'trialing' without a Stripe customer = access granted by Tappd (no card on file):
  // shown simply as Active, with no price, renewal date or checkout prompt.
  const isIncluded = status === 'trialing' && !isPaid;
  const isLive = status === 'active' || status === 'trialing';
  const periodEnd = subscription?.current_period_end && !isIncluded ? new Date(subscription.current_period_end).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : null;
  const statusLabel = {
    active: 'Active', trialing: 'Active', past_due: 'Payment failed', canceled: 'Canceled',
    pending_payment: 'Awaiting payment', expired: 'Plan ended',
  }[status] || (status || 'No subscription');

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FEF7ED' }}>
        <Loader2 className="animate-spin" size={40} style={{ color: '#8b5cf6' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FEF7ED' }}>
      <BusinessHeader user={user} />
      <div className="max-w-3xl mx-auto px-4 py-8">
        <p className="text-xs font-mono tracking-widest text-[#8b5cf6] mb-2">// subscription</p>
        <h1 className="text-3xl font-bold text-[#1e1b4b] mb-8" style={{ fontFamily: "'Playfair Display', serif" }}>Your plan</h1>

        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl p-8 border border-[#1e1b4b]/8 shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
            <div>
              <p className="text-sm text-[#1e1b4b]/60 mb-1">Current plan</p>
              <h2 className="text-2xl font-bold text-[#1e1b4b]">{planName}</h2>
              <p className="text-sm mt-1" style={{ color: isLive ? '#16a34a' : '#b45309' }}>{statusLabel}</p>
            </div>
            {tier && !isIncluded && (
              <div className="text-right">
                <p className="text-2xl font-bold text-[#1e1b4b]">
                  ${subscription?.billing_period === 'annual' ? tier.annual_price : tier.monthly_price}
                  <span className="text-sm font-normal text-[#1e1b4b]/60"> /{subscription?.billing_period === 'annual' ? 'year' : 'month'}</span>
                </p>
                {periodEnd && (
                  <p className="text-xs text-[#1e1b4b]/50">{subscription?.cancel_at_period_end ? 'Ends' : 'Renews'} {periodEnd}</p>
                )}
              </div>
            )}
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <div className="rounded-2xl p-4" style={{ background: 'rgba(139, 92, 246, 0.06)' }}>
              <p className="text-xs text-[#1e1b4b]/60 mb-1">Active deals</p>
              <p className="text-lg font-bold text-[#1e1b4b]">{subscription?.deals_used ?? 0} / {subscription?.deal_limit ?? 1}</p>
            </div>
            <div className="rounded-2xl p-4" style={{ background: 'rgba(249, 115, 22, 0.06)' }}>
              <p className="text-xs text-[#1e1b4b]/60 mb-1">Partner venues</p>
              <p className="text-lg font-bold text-[#1e1b4b]">{subscription?.locations_used ?? 0} / {subscription?.location_limit ?? 1}</p>
            </div>
          </div>

          {tier?.features?.length > 0 && (
            <ul className="space-y-2 mb-8">
              {tier.features.map((f, i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-[#1e1b4b]"><Check size={14} className="text-green-600" />{f}</li>
              ))}
            </ul>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-4 py-3 mb-4">
              <AlertCircle size={16} /> {error}
            </div>
          )}

          {isIncluded ? (
            <p className="text-sm text-[#1e1b4b]/70">
              Your plan is active. To change venues, deals or plan, email <a className="underline" href="mailto:hello@tappd.app">hello@tappd.app</a>.
            </p>
          ) : isPaid ? (
            <button
              onClick={openPortal}
              disabled={opening}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white transition-all hover:-translate-y-0.5 disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)', boxShadow: '0 8px 24px rgba(139, 92, 246, 0.3)' }}
            >
              {opening ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />}
              Manage billing
              <ExternalLink size={14} className="opacity-70" />
            </button>
          ) : (
            <button
              onClick={() => navigate('/business/subscription')}
              className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #f97316, #ea580c)' }}
            >
              <CreditCard size={18} /> Choose a plan
            </button>
          )}
          {!isIncluded && (
            <p className="text-xs text-[#1e1b4b]/50 mt-4">
              Change plan, update your card, download invoices or cancel in the secure Stripe billing portal.
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ManageSubscription;
